import React from 'react'
import styled from '@emotion/styled'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { People } from '@mui/icons-material'
import GrooverText from './GrooverText'
import { Language } from '../../../pure-js/types/Antiloop'

interface Instructor {
  id: string
  name: string
  specialties: string[]
  profilePicture?: string
}

interface PrivateLessonInstructorCardProps {
  instructor?: Instructor
  isAllInstructors?: boolean
  isSelected: boolean
  onClick: () => void
  language?: Language
}

const PrivateLessonInstructorCard: React.FC<PrivateLessonInstructorCardProps> = ({
  instructor,
  isAllInstructors = false,
  isSelected,
  onClick,
  language
}) => {
  if (isAllInstructors) {
    return (
      <AllInstructorsCard $isSelected={isSelected} onClick={onClick}>
        <CardContent>
          <IconWrapper>
            <PeopleIcon />
          </IconWrapper>
          <TextContent>
            <Title>
              <GrooverText
                textKey="privateLessonsCheckoutAllInstructors"
                language={language}
                style={{ color: '#000' }}
              />
            </Title>
            <Subtitle>
              <GrooverText
                textKey="privateLessonsCheckoutSeeAllAvailability"
                language={language}
                style={{ color: '#666' }}
              />
            </Subtitle>
          </TextContent>
        </CardContent>
        {isSelected && (
          <CheckIcon>
            <CheckCircleIcon />
          </CheckIcon>
        )}
      </AllInstructorsCard>
    )
  }

  if (!instructor) return null

  return (
    <InstructorCard $isSelected={isSelected} onClick={onClick}>
      <CardContent>
        <ProfilePicture>
          {instructor.profilePicture ? (
            <img src={instructor.profilePicture} alt={instructor.name} />
          ) : (
            <Initials>{instructor.name.charAt(0)}</Initials>
          )}
        </ProfilePicture>
        <TextContent>
          <Name>{instructor.name}</Name>
          <Specialties>
            {instructor.specialties.map((specialty, index) => (
              <SpecialtyTag key={index}>{specialty}</SpecialtyTag>
            ))}
          </Specialties>
        </TextContent>
      </CardContent>
      <RadioButton $isSelected={isSelected}>
        {isSelected && (
          <StyledCheckIcon>
            <CheckCircleIcon />
          </StyledCheckIcon>
        )}
      </RadioButton>
    </InstructorCard>
  )
}

const AllInstructorsCard = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border: 2px solid ${({ $isSelected }) => ($isSelected ? '#7c3aed' : '#e0e0e0')};
  border-radius: 12px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 16px;

  &:hover {
    border-color: ${({ $isSelected }) => ($isSelected ? '#7c3aed' : '#ccc')};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`

const InstructorCard = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border: 2px solid ${({ $isSelected }) => ($isSelected ? '#7c3aed' : '#e0e0e0')};
  border-radius: 12px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 12px;

  &:hover {
    border-color: ${({ $isSelected }) => ($isSelected ? '#7c3aed' : '#ccc')};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`

const CardContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
`

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f4ff;
  border-radius: 50%;
`

const PeopleIcon = styled(People)`
  color: #7c3aed;
  font-size: 28px;
`

const ProfilePicture = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const Initials = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #666;
`

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #000;
`

const Name = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #000;
`

const Subtitle = styled.div`
  font-size: 14px;
  color: #666;
`

const Specialties = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
`

const SpecialtyTag = styled.span`
  padding: 4px 8px;
  background: #f0f0f0;
  border-radius: 12px;
  font-size: 12px;
  color: #666;
`

const RadioButton = styled.div<{ $isSelected: boolean }>`
  width: 24px;
  height: 24px;
  border: 2px solid ${({ $isSelected }) => ($isSelected ? '#7c3aed' : '#ccc')};
  border-radius: 50%;
  background: ${({ $isSelected }) => ($isSelected ? '#7c3aed' : 'white')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const CheckIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 24px;
    color: #7c3aed;
    fill: #7c3aed;
  }
`

const StyledCheckIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 20px;
    color: white;
    fill: white;
  }
`

export default PrivateLessonInstructorCard
